import { useState, useRef } from "react";
import { Branch } from "@/types/supabase";
import { fetchWithAuth } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

// Global cache and in-flight deduplication for branches
let branchesCache: Branch[] | null = null;
let branchesCacheKey: string | null = null;
let inFlightPromise: Promise<Branch[]> | null = null;
let inFlightKey: string | null = null;
let branchesCacheTimestamp: number | null = null;
const BRANCHES_TTL_MS = 5 * 60 * 1000; // 5 minutes

export function useBranches() {
  const {
    getAccessToken,
    isManager,
    user,
    isLoading: authLoading,
    userDataLoading,
  } = useAuth();

  // Build a cache key to separate data by access scope (manager vs global)
  const cacheKey = isManager ? `manager:${user?.email ?? ""}` : "all";

  const isCacheValidForScope = () => {
    if (!branchesCache || branchesCacheKey !== cacheKey) return false;
    if (!branchesCacheTimestamp) return false;
    return Date.now() - branchesCacheTimestamp < BRANCHES_TTL_MS;
  };

  const [branches, setBranches] = useState<Branch[]>(
    branchesCache && branchesCacheKey === cacheKey ? branchesCache : [],
  );
  const [loading, setLoading] = useState(
    !(branchesCache && branchesCacheKey === cacheKey),
  );
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  // Single source of truth to fetch, with cache and deduping
  const fetchBranches = async (force = false): Promise<Branch[]> => {
    try {
      // Ensure auth is ready before fetching to avoid dashboard first-render race
      const authReady =
        !authLoading &&
        !userDataLoading &&
        (!isManager || Boolean(user?.email));
      if (!authReady) {
        setLoading(true);
        return branchesCache && branchesCacheKey === cacheKey
          ? (branchesCache as Branch[])
          : [];
      }
      // Serve from cache if available (TTL) and not forcing
      if (!force && isCacheValidForScope()) {
        setBranches(branchesCache as Branch[]);
        setError(null);
        setLoading(false);
        return branchesCache as Branch[];
      }

      // If there is an in-flight request for the same scope, await it
      if (inFlightPromise && !force && inFlightKey === cacheKey) {
        const data = await inFlightPromise;
        if (mountedRef.current) {
          setBranches(data);
          setError(null);
          setLoading(false);
        }
        return data;
      }

      setLoading(true);
      const token = await getAccessToken();

      // Mark in-flight to dedupe concurrent consumers for this scope
      inFlightKey = cacheKey;
      inFlightPromise = (async () => {
        const response = await fetchWithAuth(
          `/branch/branches?limit=100&sortDirection=desc${isManager ? `&email=${encodeURIComponent(user?.email || "")}` : ""}`,
          token,
        );
        const data = (response?.data as Branch[]) || [];
        return data;
      })();

      const data = await inFlightPromise;

      // Update cache for this scope
      branchesCache = data;
      branchesCacheKey = cacheKey;
      branchesCacheTimestamp = Date.now();

      if (mountedRef.current) {
        setBranches(data);
        setError(null);
        setLoading(false);
      }
      return data;
    } catch (err) {
      console.error("Error fetching branches:", err);
      if (mountedRef.current) {
        setError(
          err instanceof Error
            ? err.message
            : "Error al obtener las sucursales",
        );
        setLoading(false);
      }
      throw err;
    } finally {
      inFlightPromise = null;
      inFlightKey = null;
    }
  };

  return { branches, fetchBranches, loading, error };
}
